import React from 'react';
import clsx from 'clsx';
import { makeStyles, Theme } from '@material-ui/core/styles';
import TableSortLabel from '@material-ui/core/TableSortLabel';
import ArrowDownwardIcon from '@material-ui/icons/ArrowDownward';
import NamedBox from '../atoms/NamedBox';
import { useTableSort, SortDirection } from '../hooks/useTableSort';
import { CollegeProductionRow } from '../types/CollegeProductionData';

interface PuntKickoffStatRow {
  year: number;
  teamName: string;
  punt: number;
  puntYard: number;
  puntAverage: number;
  kickoff: number;
  kickoffTouchback: number;
  touchbackPct: string;
  kickoffOutOfBounds: number;
}

type ColumnAlign = 'center' | 'left';

type ColumnKey = keyof CollegeProductionRow | 'puntAverage' | 'touchbackPct';

interface ColumnConfig {
  key: ColumnKey;
  label: string;
  align: ColumnAlign;
}

const COLUMNS: ColumnConfig[] = [
  { key: 'year', label: 'Year', align: 'center' },
  { key: 'teamName', label: 'Team', align: 'left' },
  { key: 'punt', label: 'Punts', align: 'center' },
  { key: 'puntYard', label: 'Punt Yards', align: 'center' },
  { key: 'puntAverage', label: 'Punt Average', align: 'center' },
  { key: 'kickoff', label: 'Kickoffs', align: 'center' },
  { key: 'kickoffTouchback', label: 'Touchbacks', align: 'center' },
  { key: 'touchbackPct', label: 'Touchback %', align: 'center' },
  { key: 'kickoffOutOfBounds', label: 'KO OOB', align: 'center' },
];

/**
 * Registry of derived columns. Each entry knows how to compute its raw numeric
 * value from a row (used for sorting) and how to format it for display.
 * Add a new computed column here without touching render/sort logic.
 */
const COMPUTED_COLUMNS: Record<string, {
  compute: (row: CollegeProductionRow) => number;
  format: (value: number) => string;
}> = {
  puntAverage: {
    compute: row => (row.punt ? row.puntYard / row.punt : 0),
    format: value => value.toFixed(1),
  },
  touchbackPct: {
    compute: row => (row.kickoff ? (row.kickoffTouchback / row.kickoff) * 100 : 0),
    format: value => `${value.toFixed(1)}%`,
  },
};

// const data: PuntKickoffStatRow[] = [
//   {
//     year: 2025,
//     teamName: 'Purdue',
//     punt: 50,
//     puntYard: 2126,
//     puntAverage: 43.2,
//     kickoff: 0,
//     kickoffTouchback: 0,
//     touchbackPct: '0.0%',
//     kickoffOutOfBounds: 0,
//   },
// ];

const useStyles = makeStyles((theme: Theme) => ({
  root: {},
  headerName: {
    fontFamily: 'inherit',
  },
  tableWrapper: {
    width: '100%',
    overflowX: 'auto' as const,
    backgroundColor: '#ffffff',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse' as const,
    backgroundColor: '#ffffff',
    fontSize: theme.typography.pxToRem(13),
  },
  theadRow: {
    height: 40,
    backgroundColor: '#ffffff',
    borderBottom: '1px solid #e0e0e0',
  },
  th: {
    fontSize: theme.typography.pxToRem(13),
    fontWeight: 600,
    color: '#1e1e1e',
    padding: theme.spacing(0.75, 1),
    whiteSpace: 'nowrap' as const,
    borderRight: '1px solid #f0f0f0',
    userSelect: 'none' as const,
    cursor: 'pointer',
    '&:last-child': {
      borderRight: 'none',
    },
  },
  alignCenter: {
    textAlign: 'center' as const,
  },
  alignLeft: {
    textAlign: 'left' as const,
  },
  bodyRow: {
    height: 44,
    transition: 'background-color 0.15s ease',
    '&:nth-child(odd)': {
      backgroundColor: '#f5f5f5',
    },
    '&:nth-child(even)': {
      backgroundColor: '#ffffff',
    },
    '&:hover': {
      backgroundColor: '#f8f8f8',
    },
  },
  td: {
    fontSize: theme.typography.pxToRem(13),
    fontWeight: 500,
    color: '#1e1e1e',
    padding: theme.spacing(0.75, 1),
    whiteSpace: 'nowrap' as const,
    borderRight: '1px solid #f0f0f0',
    '&:last-child': {
      borderRight: 'none',
    },
  },
  sortLabelIcon: {
    opacity: 1,
  },
}), { name: 'SpecialTeamYearlyPuntKickoffStatsTable' });

interface SpecialTeamYearlyPuntKickoffStatsTableProps {
  className?: string;
  data?: CollegeProductionRow[] | null;
}

export default function SpecialTeamYearlyPuntKickoffStatsTable(props: SpecialTeamYearlyPuntKickoffStatsTableProps) {
  const { className, data = [] } = props;
  const classes = useStyles();

  const getSortValue = (row: CollegeProductionRow, key: ColumnKey): number | string => {
    const computed = COMPUTED_COLUMNS[key as string];
    if (computed) {
      return computed.compute(row);
    }
    return row[key as keyof CollegeProductionRow] as number | string;
  };

  const { sortKey, sortDirection, handleSort, sortedRows: sortedData } = useTableSort<CollegeProductionRow, ColumnKey>({
    rows: data,
    initialSortKey: 'year',
    initialDirection: 'desc',
    nextKeyDirection: 'desc',
    sortRows: (rows, key, direction) => {
      rows.sort((first, second) => {
        const firstValue = getSortValue(first, key);
        const secondValue = getSortValue(second, key);

        let comparison = 0;
        if (typeof firstValue === 'number' && typeof secondValue === 'number') {
          comparison = firstValue - secondValue;
        } else {
          comparison = String(firstValue).localeCompare(String(secondValue));
        }

        return direction === 'asc' ? comparison : -comparison;
      });

      return rows;
    },
  });

  return (
    <NamedBox
      className={clsx(classes.root, className)}
      headerNameClassName={classes.headerName}
      name="Yearly Punt & Kickoff Stats"
      white
    >
      <div className={classes.tableWrapper}>
        <table className={classes.table}>
          <thead>
            <tr className={classes.theadRow}>
              {COLUMNS.map(column => {
                const alignClass = column.align === 'left' ? classes.alignLeft : classes.alignCenter;
                const isActive = column.key === sortKey;

                return (
                  <th
                    key={column.key}
                    className={clsx(classes.th, alignClass)}
                  >
                    <TableSortLabel
                      active={isActive}
                      direction={isActive ? sortDirection : 'asc'}
                      hideSortIcon={false}
                      IconComponent={(iconProps) => (
                        <ArrowDownwardIcon
                          {...iconProps}
                          style={{
                            color: isActive ? '#2f6bff' : '#b5b5b5',
                          }}
                        />
                      )}
                      classes={{ icon: classes.sortLabelIcon }}
                      onClick={() => handleSort(column.key)}
                    >
                      {column.label}
                    </TableSortLabel>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {sortedData.map(row => (
              <tr key={row.year} className={classes.bodyRow}>
                {COLUMNS.map(column => {
                  const alignClass = column.align === 'left' ? classes.alignLeft : classes.alignCenter;
                  const computed = COMPUTED_COLUMNS[column.key as string];
                  const cellValue = computed
                    ? computed.format(computed.compute(row))
                    : row[column.key as keyof CollegeProductionRow];
                  return (
                    <td key={column.key} className={clsx(classes.td, alignClass)}>
                      {cellValue}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </NamedBox>
  );
}
